import React from "react";
import { useNavigate } from "react-router-dom";
import { Zap, ArrowLeft } from "lucide-react";

const LegalShell = ({ title, updated, children }) => {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen text-slate-200" style={{ background: "#07080D" }} data-testid="legal-page">
      <header className="border-b" style={{ borderColor: "rgba(99,102,241,0.18)" }}>
        <div className="max-w-3xl mx-auto px-5 sm:px-8 h-16 flex items-center gap-3">
          <button onClick={() => navigate("/")} className="flex items-center gap-2.5" data-testid="legal-home-link">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: "linear-gradient(135deg,#6366F1,#8B5CF6)" }}><Zap className="w-4 h-4 text-white" /></div>
            <span className="font-display font-extrabold text-lg">Momento</span>
          </button>
          <button onClick={() => navigate("/")} className="ml-auto inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-white transition-colors"><ArrowLeft className="w-4 h-4" />Back to home</button>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-5 sm:px-8 py-14">
        <h1 className="font-display font-extrabold text-3xl sm:text-4xl">{title}</h1>
        <p className="text-slate-500 text-sm mt-2">Last updated: {updated}</p>
        <div className="mt-10 space-y-8">{children}</div>
        <p className="text-slate-500 text-sm mt-14 border-t pt-6" style={{ borderColor: "rgba(148,163,184,0.14)" }}>
          Questions about your data? Contact us at <a href="mailto:info@momentocrm.com" className="text-indigo-400 hover:text-indigo-300">info@momentocrm.com</a>.
        </p>
      </main>
    </div>
  );
};

const Section = ({ n, title, children }) => (
  <section>
    <h2 className="font-display font-bold text-xl text-white">{n}. {title}</h2>
    <div className="text-slate-300 text-[15px] leading-relaxed mt-3 space-y-3">{children}</div>
  </section>
);

export default function PrivacyPolicy() {
  return (
    <LegalShell title="Privacy Policy" updated="June 2026">
      <p className="text-slate-300 text-[15px] leading-relaxed">
        This Privacy Policy explains how Momento ("we", "us") collects, uses, and protects personal information when
        you use our platform (the "Service"). We act as a data controller for account information and as a data
        processor for the contact data our customers store in Momento.
      </p>

      <Section n="1" title="Information We Collect">
        <p><strong>Account data:</strong> name, email, agency name, and login credentials.</p>
        <p><strong>Billing data:</strong> subscription and payment details, processed and stored by Stripe; we do
          not store full card numbers.</p>
        <p><strong>Customer Data:</strong> the contacts, messages, and records you and your clients add to the
          platform.</p>
        <p><strong>Usage data:</strong> log data, device/browser information, and analytics about how the Service
          is used.</p>
      </Section>

      <Section n="2" title="How We Use Information">
        <p>We use information to provide and improve the Service, process subscriptions and payments, send
          transactional and service emails, provide support, ensure security, and comply with legal obligations.</p>
      </Section>

      <Section n="3" title="Legal Bases (GDPR)">
        <p>Where applicable, we process personal data based on performance of a contract, our legitimate interests
          in operating and securing the Service, your consent (which you can withdraw), and compliance with legal
          obligations.</p>
      </Section>

      <Section n="4" title="Sharing & Sub-processors">
        <p>We share data with service providers who help us operate the Service, including payment processing
          (Stripe), email delivery, messaging providers, cloud hosting, and analytics. These providers process data
          on our behalf under appropriate agreements. We do not sell your personal information.</p>
      </Section>

      <Section n="5" title="Data Retention">
        <p>We retain account and Customer Data for as long as your account is active and for a reasonable period
          afterwards, unless a longer retention is required by law. You can request deletion as described below.</p>
      </Section>

      <Section n="6" title="Security">
        <p>We use technical and organizational measures such as encryption in transit, access controls, and secure
          credential storage to protect your data. No method of transmission or storage is completely secure, but
          we work to protect your information.</p>
      </Section>

      <Section n="7" title="Your Rights">
        <p>Subject to applicable law, you may request access to, correction of, or deletion of your personal data,
          object to or restrict certain processing, and request data portability. To exercise these rights, contact
          info@momentocrm.com. If you are an end-contact of one of our customers, please contact that customer
          directly, as they control that data.</p>
      </Section>

      <Section n="8" title="Cookies & Analytics">
        <p>We use cookies and similar technologies to keep you signed in, remember preferences, and understand
          usage. You can control cookies through your browser settings.</p>
      </Section>

      <Section n="9" title="International Transfers">
        <p>Your information may be processed in countries other than your own. Where required, we use appropriate
          safeguards for such transfers.</p>
      </Section>

      <Section n="10" title="Changes & Contact">
        <p>We may update this Policy from time to time and will notify you of material changes. For any privacy
          questions or requests, contact us at info@momentocrm.com.</p>
      </Section>
    </LegalShell>
  );
}
